import { Component, Inject, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatDialogRef, MAT_DIALOG_DATA, MatDialogModule } from '@angular/material/dialog';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatRippleModule } from '@angular/material/core';
import { FormsModule } from '@angular/forms';
import { MenuNode } from '../models/role-menu-mapping.model';



@Component({
    selector: 'app-add-menu-dialog',
    standalone: true,
    imports: [
        CommonModule,
        MatDialogModule,
        MatButtonModule,
        MatIconModule,
        MatCheckboxModule,
        MatRippleModule,
        FormsModule
    ],
    template: `
        <div class="dialog-container">
            <div class="dialog-header">
                <div>
                    <h2 class="dialog-title">
                        <mat-icon>add_circle_outline</mat-icon>
                        Menus
                    </h2>
                </div>
                <!-- Selection count -->
                <div class="selection-badge" *ngIf="selectedCount > 0">
                    {{ selectedCount }} Selected
                </div>
            </div>

            <mat-dialog-content class="dialog-content">
                <div class="list-container">
                    <ng-container *ngIf="data.menus && data.menus.length > 0; else noData">
                        <div class="menu-card" 
                             *ngFor="let menu of data.menus" 
                             matRipple 
                             [class.selected]="menu.selected"
                             (click)="toggleMenu(menu)">
                            
                            <div class="menu-icon-wrapper">
                                <mat-icon class="menu-icon">dashboard</mat-icon>
                            </div>
                            
                            <div class="menu-details">
                                <span class="menu-name">{{ menu.menuName }}</span>
                            </div>
                            
                            <div class="menu-action" (click)="$event.stopPropagation()">
                                <mat-checkbox 
                                    [(ngModel)]="menu.selected" 
                                    (change)="updateSelection()"
                                    color="primary">
                                </mat-checkbox>
                            </div>
                        </div>
                    </ng-container>

                    <ng-template #noData>
                        <div class="empty-state">
                            <mat-icon class="empty-icon">inbox</mat-icon>
                            <h3>All Caught Up!</h3>
                            <p>There are no more menus available to add.</p>
                        </div>
                    </ng-template>
                </div>
            </mat-dialog-content>

            <mat-dialog-actions align="end" class="dialog-actions">
                <button mat-button class="cancel-btn" (click)="onCancel()" tabindex="-1">Cancel</button>
                <button mat-raised-button color="primary" class="save-btn" (click)="onSave()">
                    <mat-icon>check</mat-icon>
                    Assign Menus
                </button>
            </mat-dialog-actions>
        </div>
    `,
    styles: [`
        .dialog-container {
            display: flex;
            flex-direction: column;
            background: #ffffff;
            border-radius: 16px;
            overflow: hidden;
            width: 100%;
        }

        ::ng-deep .mat-mdc-dialog-container .mdc-dialog__surface {
            padding: 0 !important;
            border-radius: 16px !important;
            overflow: hidden !important;
        }

        .dialog-header {
            background: linear-gradient(135deg, #2e7d32 0%, #4caf50 100%);
            color: white;
            padding: 16px 20px;
            display: flex;
            justify-content: space-between;
            align-items: center;
            box-shadow: 0 4px 12px rgba(0,0,0,0.1);
            position: relative;
            z-index: 2;
        }

        .dialog-title {
            margin: 0;
            display: flex;
            align-items: center;
            gap: 8px;
            font-size: 18px;
            font-weight: 600;
            letter-spacing: 0.5px;
        }

        .dialog-title mat-icon {
            font-size: 15px;
            width: 15px;
            height: 15px;
        }

        .dialog-subtitle {
            margin: 4px 0 0 28px;
            font-size: 13px;
            opacity: 0.85;
            font-weight: 400;
        }

        .selection-badge {
            background: rgba(255,255,255,0.2);
            padding: 4px 10px;
            border-radius: 16px;
            font-size: 12px;
            font-weight: 600;
            backdrop-filter: blur(4px);
            border: 1px solid rgba(255,255,255,0.3);
            animation: popIn 0.3s ease-out forwards;
        }

        .dialog-content {
            padding: 16px 20px !important;
            max-height: 50vh;
            overflow-y: auto;
            background: #f8f9fa;
        }

        /* Scrollbar styling */
        .dialog-content::-webkit-scrollbar {
            width: 8px;
        }
        .dialog-content::-webkit-scrollbar-track {
            background: transparent;
        }
        .dialog-content::-webkit-scrollbar-thumb {
            background: #d5edcc;
            border-radius: 4px;
        }
        .dialog-content::-webkit-scrollbar-thumb:hover {
            background: #7ea17f;
        }

        .list-container {
            display: flex;
            flex-direction: column;
            gap: 8px;
            padding-bottom: 4px;
        }

        .menu-card {
            display: flex;
            align-items: center;
            background: white;
            border-radius: 8px;
            padding: 10px 14px;
            box-shadow: 0 1px 3px rgba(0,0,0,0.04);
            border: 1px solid #eef2f6;
            transition: all 0.3s cubic-bezier(0.25, 0.8, 0.25, 1);
            cursor: pointer;
            position: relative;
            overflow: hidden;
            opacity: 0;
            animation: slideUpFade 0.5s cubic-bezier(0.25, 0.8, 0.25, 1) forwards;
        }

        .menu-card:nth-child(1) { animation-delay: 0.05s; }
        .menu-card:nth-child(2) { animation-delay: 0.1s; }
        .menu-card:nth-child(3) { animation-delay: 0.15s; }
        .menu-card:nth-child(4) { animation-delay: 0.2s; }
        .menu-card:nth-child(5) { animation-delay: 0.25s; }
        .menu-card:nth-child(6) { animation-delay: 0.3s; }
        .menu-card:nth-child(7) { animation-delay: 0.35s; }
        .menu-card:nth-child(8) { animation-delay: 0.4s; }
        .menu-card:nth-child(9) { animation-delay: 0.45s; }
        .menu-card:nth-child(10) { animation-delay: 0.5s; }
        .menu-card:nth-child(n+11) { animation-delay: 0.55s; }

        .menu-card:hover {
            transform: translateY(-2px);
            box-shadow: 0 6px 16px rgba(0, 0, 0, 0.08);
            border-color: #d0e2d4;
        }

        .menu-card.selected {
            background: #dbf1da;
            border-color: #c8e6c9;
            box-shadow: 0 4px 12px rgba(76, 175, 80, 0.08);
        }
        
        .menu-card.selected::before {
            content: '';
            position: absolute;
            left: 0;
            top: 0;
            bottom: 0;
            width: 4px;
            background: #4caf50;
            border-top-left-radius: 8px;
            border-bottom-left-radius: 8px;
        }

        .menu-icon-wrapper {
            background: #eef2f6;
            width: 32px;
            height: 32px;
            border-radius: 6px;
            display: flex;
            align-items: center;
            justify-content: center;
            margin-right: 12px;
            color: #64748b;
            transition: all 0.3s ease;
        }
        
        .menu-icon-wrapper .menu-icon {
            font-size: 18px;
            width: 18px;
            height: 18px;
        }

        .menu-card.selected .menu-icon-wrapper {
            background: #e8f5e9;
            color: #4caf50;
        }

        .menu-details {
            flex: 1;
            display: flex;
            flex-direction: column;
        }

        .menu-name {
            font-size: 14px;
            font-weight: 600;
            color: #1e293b;
            margin-bottom: 2px;
        }

        .menu-id {
            font-size: 12px;
            color: #648b65;
        }

        .menu-action {
            display: flex;
            align-items: center;
            justify-content: center;
            margin-left: 8px;
        }

        ::ng-deep .menu-action .mdc-checkbox {
            padding: 2px;
            margin: 0 !important;
            background: transparent !important;
        }

        /* Hide the misaligned Material ripple/touch circle behind the checkbox in dialog */
        ::ng-deep .menu-action .mdc-checkbox__ripple,
        ::ng-deep .menu-action .mat-ripple-element,
        ::ng-deep .menu-action .mat-mdc-checkbox-ripple,
        ::ng-deep .menu-action .mat-mdc-focus-indicator,
        ::ng-deep .menu-action .mdc-checkbox__background::before,
        ::ng-deep .menu-action .mat-mdc-checkbox-touch-target {
            display: none !important;
            opacity: 0 !important;
            background: transparent !important;
        }

        .empty-state {
            text-align: center;
            padding: 40px 20px;
            color: #648b65;
            animation: fadeIn 0.5s ease-out;
        }

        .empty-icon {
            font-size: 48px;
            width: 48px;
            height: 48px;
            color: #cbd5e1;
            margin-bottom: 8px;
        }

        .empty-state h3 {
            margin: 0 0 8px 0;
            color: #334155;
            font-size: 20px;
        }

        .empty-state p {
            margin: 0;
            font-size: 14px;
        }

        .dialog-actions {
            background: #ffffff;
            padding: 12px 20px !important;
            margin: 0;
            border-top: 1px solid #eef2f6;
            display: flex;
            gap: 12px;
        }

        .cancel-btn {
            color: #6c8b64;
            font-weight: 500;
            border-radius: 6px;
            padding: 0 16px;
            height: 36px;
        }

        .cancel-btn:hover {
            background: #f1f5f9;
        }

        .save-btn {
            border-radius: 6px;
            padding: 0 20px;
            height: 36px;
            font-weight: 600;
            letter-spacing: 0.3px;
            box-shadow: 0 4px 10px rgba(76, 175, 80, 0.3);
            display: flex;
            align-items: center;
            gap: 6px;
        }

        .save-btn mat-icon {
            font-size: 20px;
            width: 20px;
            height: 20px;
            margin-right: 4px;
        }

        @keyframes popIn {
            0% { transform: scale(0.8); opacity: 0; }
            100% { transform: scale(1); opacity: 1; }
        }

        @keyframes fadeIn {
            0% { opacity: 0; transform: translateY(10px); }
            100% { opacity: 1; transform: translateY(0); }
        }

        @keyframes slideUpFade {
            0% { opacity: 0; transform: translateY(20px); }
            100% { opacity: 1; transform: translateY(0); }
        }
    `]
})
export class AddMenuDialogComponent implements OnInit {
    selectedCount = 0;

    constructor(
        public dialogRef: MatDialogRef<AddMenuDialogComponent>,
        @Inject(MAT_DIALOG_DATA) public data: { menus: MenuNode[] }
    ) { }

    ngOnInit() {
        this.updateSelection();
    }

    toggleMenu(menu: MenuNode): void {
        menu.selected = !menu.selected;
        this.updateSelection();
    }

    updateSelection(): void {
        this.selectedCount = this.data.menus.filter(m => m.selected).length;
    }

    onSave(): void {
        const selectedMenus = this.data.menus.filter(m => m.selected);
        this.dialogRef.close(selectedMenus);
    }

    onCancel(): void {
        this.dialogRef.close();
    }
}
